<template>
  <div
    v-if="isListingSupportedByTravelTime && (hasDefaultPOI || hasUserPOI)"
    :class="$style.customPOI"
    data-testid="travel-time-custom-poi"
  >
    <header :class="$style.customPOIHeader">
      <h3 :class="$style.customPOITitle">{{ t('travelTimeCustomPOI.title') }}</h3>
      <div :class="[$style.customPOIHeaderRight, isLoading ? $style.loading : '']">
        <HgIcon
          v-if="hasUserPOI"
          icon="Delete"
          :color="getIconColor"
          theme="current"
          @click="handleDeleteClick()"
        />
        <HgIcon icon="Edit" :color="getIconColor" theme="current" @click="openEditPoiModal()" />
      </div>
    </header>
    <ul :class="customPOI.length > 1 ? $style.customPOIList : $style.customPOISingleItem">
      <li v-for="timing in customPOI" :key="timing.key">
        <div :class="$style.customPOIListContent">
          <HgIcon v-if="hasTransportationIcon(timing)" :icon="formatTransportationIcon(timing)" />
          <span :class="$style.customPOIListTime">{{ formatCommuteTime(timing.time) }}</span>
          <span :class="$style.customPOIListDescription">{{ formatStationName(timing.name) }}</span>
        </div>
      </li>
    </ul>
    <CustomPOIModal
      :addressSuggestions="customPoiAddressSuggestions"
      :isVisible="isCustomPOIModalVisible"
      :customPoi="travelTimeStore.getCustomPOI"
      :translations="customPoiTranslations"
      :platform="SupportedPlatforms.hg"
      :tracking="trackingSigninModal"
      :isRequestPending="isRequestPending"
      @addressEdited="addressEdited($event)"
      @closeModal="closeModal()"
      @saveCustomPoi="saveCustomPoi($event)"
    />
    <SigninModal
      v-if="showSignInModal"
      :isVisible="showSignInModal"
      :translations="customPoiSigninModalTranslations"
      :image="customPoiSigninModalImage"
      @close="closeCustomPoiSignInModal"
      @signin="handleSignin"
      @register="handleRegister"
    />
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable max-lines */
import { datadogRum } from '@datadog/browser-rum';
import { HgIcon, Icon, SupportedPlatforms } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import {
  CustomPOIModal,
  TravelTimeTransportationType as ModalTransportTypes,
  SigninModal,
} from '@smg-real-estate/seeker-app-shared-components';
import { computed, defineAsyncComponent, onMounted, ref } from 'vue';

import {
  getAuthToken,
  getUserLoginInfo,
  loginWithRedirect,
} from '@/helpers/Authentication/authClient';
import {
  getRedirectState,
  RedirectAction,
  removeRedirectState,
} from '@/helpers/Authentication/redirect';
import { LoginTargetAction, ReferringFeature } from '@/helpers/Authentication/types';
import { EventLabel, InteractionEvent, InteractionGoal } from '@/helpers/GTM/GTMParameters';
import { trackEventHarmonized } from '@/helpers/GTM/GTMTrack';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { formatTravelTime } from '@/helpers/travel-time-helpers';
import { useTravelTimeStore } from '@/store/modules/travel-time';
import { TravelTimeData, TravelTimeTransportationType } from '@/store/types/travel-time-state';
import { useI18n } from 'vue-i18n';
import {
  ensureGoogleLoaded,
  getPlaceDetailsAsync,
  isListingSupportedByTravelTime as isListingSupportedByTravelTimeHelper,
} from './helper';
import { modalTranslations } from './modalTranslations';

const HgImage = defineAsyncComponent(
  () => import('@/assets/favourites/hg_required_login_modal.svg'),
);
const Is24Image = defineAsyncComponent(
  () => import('@/assets/favourites/is24_required_login_modal.svg'),
);

export interface AddressSuggestion {
  displayValue: string;
  placeId: string;
}

const TRANSPORTATION_ICON_MAP: Record<ModalTransportTypes, Icon> = {
  [ModalTransportTypes.WALKING]: Icon.Walking,
  [ModalTransportTypes.PUBLIC_TRANSPORT]: Icon.Train,
  [ModalTransportTypes.BICYCLE]: Icon.Bicycle,
  [ModalTransportTypes.CAR]: Icon.Car,
};
const TRACKING_TRANSPORT_TYPE_MAP: Record<ModalTransportTypes, EventLabel> = {
  [ModalTransportTypes.BICYCLE]: EventLabel.POI_PDP_CYCLING,
  [ModalTransportTypes.WALKING]: EventLabel.POI_PDP_WALKING,
  [ModalTransportTypes.CAR]: EventLabel.POI_PDP_DRIVING,
  [ModalTransportTypes.PUBLIC_TRANSPORT]: EventLabel.POI_PDP_PUBLIC_TRANSPORT,
};

const travelTimeStore = useTravelTimeStore();
const { t } = useI18n();

defineOptions({
  name: 'TravelTimeCustomPOI',
});

interface Props {
  listing: Listing;
}
const props = defineProps<Props>();

const trackingSigninModal = {
  onDisplayModal: () =>
    trackEventHarmonized(InteractionEvent.FEATURE_DISPLAYED, {
      h_event_category_006: InteractionEvent.FEATURE_DISPLAYED,
      h_event_action_007: InteractionGoal.DISPLAY_LOGIN_MODAL,
      h_event_label_008: EventLabel.POI_PDP,
    }),
  onSigninClick: () =>
    trackEventHarmonized(InteractionEvent.CTA_CLICK, {
      h_event_category_006: InteractionEvent.CTA_CLICK,
      h_event_action_007: InteractionGoal.GO_TO_LOGIN,
      h_event_label_008: EventLabel.POI_PDP,
    }),
  onRegisterClick: () =>
    trackEventHarmonized(InteractionEvent.CTA_CLICK, {
      h_event_category_006: InteractionEvent.CTA_CLICK,
      h_event_action_007: InteractionGoal.CREATE_ACCOUNT,
      h_event_label_008: EventLabel.POI_PDP,
    }),
};

const googlePlaceAutocomplete = ref<google.maps.places.AutocompleteService>();
const googlePlaceSessionToken = ref<typeof google.maps.places.AutocompleteSessionToken>();
const googlePlacesService = ref<google.maps.places.PlacesService>();
const token = ref<google.maps.places.AutocompleteSessionToken>();
const isAuth = ref(false);
const isIconAvailable = ref(true);
const isCustomPOIModalVisible = ref(false);
const isRequestPending = ref(false);
const customPoiAddressSuggestions = ref<AddressSuggestion[]>([]);
const showSignInModal = ref(false);

const customPoiTranslations = computed(() => modalTranslations(t));
const customPOI = computed<TravelTimeData>(() =>
  travelTimeStore.getCustomPOITravelTimeData(props.listing.id!),
);
const hasUserPOI = computed(() => travelTimeStore.hasUserPOI);
const hasCustomPOI = computed(() => customPOI.value.length > 0);
const hasDefaultPOI = computed(() =>
  travelTimeStore.hasDefaultPOI.some((poi) => poi.id === props.listing.id),
);
const isListingSupportedByTravelTime = computed(() =>
  isListingSupportedByTravelTimeHelper(props.listing),
);
const formatStationName = computed(() => {
  return (name: string) => {
    return (!hasUserPOI.value ? t('listingTravelTime.station') + ': ' : '') + name;
  };
});
const getIconColor = computed(() => (isIconAvailable.value ? '#1e50ad' : 'gray'));
const isLoading = computed((): boolean => travelTimeStore.loading);
const customPoiSigninModalTranslations = computed(() => {
  return {
    title: t('travelTimeCustomPOI.signinModal.title').toString(),
    description: t('travelTimeCustomPOI.signinModal.description').toString(),
    signinButtonText: t('travelTimeCustomPOI.signinModal.signinBtn').toString(),
    registerButtonText: t('travelTimeCustomPOI.signinModal.registerBtn').toString(),
  };
});
const customPoiSigninModalImage = computed(() => {
  return isPlatformIs24() ? Is24Image : HgImage;
});

onMounted(() => {
  if (!isListingSupportedByTravelTime.value) {
    return;
  }
  // Load POI data asynchronously without blocking main thread
  loadCustomPOIAsync();
  handleRedirectAction();
});

/**
 * Load custom POI data asynchronously without blocking main thread
 * KK-1450: Performance improvement for POI loading
 */
const loadCustomPOIAsync = async (): Promise<void> => {
  try {
    // Prepare promises for parallel execution
    const promises: Promise<any>[] = [];

    // Load custom POI if not already present
    if (!hasCustomPOI.value || !hasUserPOI.value) {
      promises.push(
        travelTimeStore.loadCustomPOI({
          listings: [props.listing],
        }),
      );
    }

    // Load default POI data
    promises.push(
      travelTimeStore.fetchDefaultPOIDataNoCommit({
        listing: props.listing,
      }),
    );

    // Fetch user custom POI
    promises.push(travelTimeStore.fetchCustomPOI());

    // Execute all POI loading in parallel without blocking
    await Promise.allSettled(promises);
  } catch (error) {
    // Handle errors gracefully without blocking UI
    datadogRum.addError(error);
  }
};

const formatCommuteTime = (seconds: number): string | undefined => {
  const timeInMin = seconds / 60;
  if (timeInMin < 1) {
    return '< 1 min.';
  }

  return formatTravelTime(seconds, hasUserPOI.value);
};
const hasTransportationIcon = (poiPayload: { transportation?: TravelTimeTransportationType }) => {
  return (
    !!poiPayload.transportation &&
    Object.keys(TRANSPORTATION_ICON_MAP).includes(poiPayload.transportation)
  );
};
const formatTransportationIcon = (poiPayload: {
  transportation?: TravelTimeTransportationType;
}) => {
  const transportationType = poiPayload.transportation as TravelTimeTransportationType;
  return TRANSPORTATION_ICON_MAP[transportationType];
};
const closeModal = () => {
  isCustomPOIModalVisible.value = false;
  trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
    h_event_category_006: InteractionEvent.PDP_INTERACTION,
    h_event_action_007: InteractionGoal.CLOSE_POI_MODAL,
    h_event_label_008: EventLabel.POI_PDP,
  });
};
// skipLoad on redirect from auth isLoading is set to true
const openEditPoiModal = async (skipLoad = false) => {
  trackEventHarmonized(InteractionEvent.FEATURE_DISPLAYED, {
    h_event_category_006: InteractionEvent.FEATURE_DISPLAYED,
    h_event_action_007: InteractionGoal.DISPLAY_POI_MODAL,
    h_event_label_008: EventLabel.POI_PDP,
  });
  if (isLoading.value && !skipLoad) {
    return;
  }
  await authorize();
  if (!isAuth.value) {
    return;
  }
  await loadGooglePlaces();
  isCustomPOIModalVisible.value = true;
};
const generateToken = () => {
  if (googlePlaceSessionToken.value) {
    return new googlePlaceSessionToken.value();
  }
};
const loadGooglePlaces = async () => {
  try {
    if (googlePlaceAutocomplete.value && googlePlacesService.value) {
      token.value = generateToken();
      return;
    }
    await ensureGoogleLoaded(import.meta.env.VUE_APP_PDP_GOOGLE_PLACES_API_KEY);
    googlePlaceAutocomplete.value = new globalThis.google.maps.places.AutocompleteService();
    googlePlacesService.value = new globalThis.google.maps.places.PlacesService(
      document.createElement('div'),
    );
    googlePlaceSessionToken.value = globalThis.google.maps.places.AutocompleteSessionToken;
    token.value = generateToken();
  } catch (error) {
    datadogRum.addError(error);
  }
};
const authorize = async () => {
  isAuth.value = !!(await getAuthToken());
  if (!isAuth.value) {
    showSignInModal.value = true;
  }
};
const handleDeleteClick = async () => {
  if (isLoading.value) {
    return;
  }
  await authorize();
  if (isAuth.value && hasUserPOI.value) {
    try {
      await travelTimeStore.deleteCustomPOI({ id: travelTimeStore.getCustomPOI!.id });
      trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
        h_event_category_006: InteractionEvent.PDP_INTERACTION,
        h_event_action_007: InteractionGoal.DELETE_POI,
        h_event_label_008: EventLabel.POI_PDP,
      });
      await travelTimeStore.fetchCustomPOITravelTimeData({
        listings: [props.listing],
      });
    } catch (error) {
      datadogRum.addError(error);
    }
  }
};
const addressEdited = async (event: any) => {
  try {
    if (!googlePlaceAutocomplete.value) {
      return;
    }
    const request = {
      input: event,
      language: 'en-US',
      region: 'ch',
      componentRestrictions: {
        country: 'ch',
      },
      sessionToken: token.value,
    } as google.maps.places.AutocompletionRequest;
    const googlePlacesRes = await googlePlaceAutocomplete.value.getPlacePredictions(request);
    customPoiAddressSuggestions.value = googlePlacesRes.predictions
      ? (googlePlacesRes.predictions
          .filter((place) => place.description && place.place_id)
          .map((place) => ({
            placeId: place.place_id,
            displayValue: place.description,
          })) as AddressSuggestion[])
      : [];
  } catch {
    customPoiAddressSuggestions.value = [];
  }
};
const saveCustomPoi = async (event: any) => {
  if (isAuth.value) {
    await dispatchEvent(event);
  }
};
const enrichEventWithPlaceId = async (event: any) => {
  try {
    let lat = event.lat;
    let lng = event.lng;
    let place;
    if (event.placeId && token.value) {
      place = await getPlaceDetailsAsync(
        token.value,
        event.placeId,
        googlePlacesService.value as google.maps.places.PlacesService,
      );
    }
    if (place) {
      lat = place.geometry?.location?.lat();
      lng = place.geometry?.location?.lng();
    }
    return {
      id: event.id,
      name: event.name,
      transportationType: event.transportationType,
      address: event.address,
      lat: lat,
      lng: lng,
    };
  } catch (error) {
    datadogRum.addError(error);
  }
};
const dispatchEvent = async (event: any) => {
  try {
    isRequestPending.value = true;
    const request = await enrichEventWithPlaceId(event);
    if (!request) {
      isRequestPending.value = false;
      return;
    }
    const updateOrCreate = !!travelTimeStore.getCustomPOI;
    const res = updateOrCreate
      ? await travelTimeStore.updateCustomPOI(request)
      : await travelTimeStore.createCustomPOI(request);

    if (res?.id) {
      trackEventHarmonized(InteractionEvent.PDP_INTERACTION, {
        h_event_category_006: InteractionEvent.PDP_INTERACTION,
        h_event_action_007: updateOrCreate ? InteractionGoal.EDIT_POI : InteractionGoal.CREATE_POI,
        h_event_label_008:
          TRACKING_TRANSPORT_TYPE_MAP[request.transportationType as ModalTransportTypes],
      });
      await travelTimeStore.fetchUserPOITravelTimeData({
        listings: [props.listing],
      });
      isCustomPOIModalVisible.value = false;
    }
    isRequestPending.value = false;
  } catch (error) {
    isRequestPending.value = false;
    datadogRum.addError(error);
  }
};
const handleSignin = async () => {
  await loginWithRedirect(
    LoginTargetAction.login,
    RedirectAction.SAVE_CUSTOM_POI_PDP,
    { eventAction: 'auth_login' },
    ReferringFeature.POI_PDP,
  );
};
const handleRegister = async () => {
  await loginWithRedirect(
    LoginTargetAction.signup,
    RedirectAction.SAVE_CUSTOM_POI_PDP,
    { eventAction: 'auth_sign_up' },
    ReferringFeature.POI_PDP,
  );
};
const closeCustomPoiSignInModal = () => {
  trackEventHarmonized(InteractionEvent.CTA_CLICK, {
    h_event_category_006: InteractionEvent.CTA_CLICK,
    h_event_action_007: InteractionGoal.CLOSE_LOGIN_MODAL,
    h_event_label_008: EventLabel.POI_PDP,
  });
  showSignInModal.value = false;
};
const handleRedirectAction = async (): Promise<void> => {
  const redirectState = getRedirectState();
  const isUserLoggedIn = await getUserLoginInfo();
  if (isUserLoggedIn && redirectState?.redirectAction === RedirectAction.SAVE_CUSTOM_POI_PDP) {
    trackEventHarmonized(InteractionEvent.AUTHENTICATION, {
      h_event_category_006: InteractionEvent.AUTHENTICATION,
      h_event_action_007: redirectState.redirectPayload?.eventAction,
      h_event_label_008: EventLabel.SUCCESSFUL,
      h_login_location_049: EventLabel.POI_PDP,
    });
    await openEditPoiModal(true);
    removeRedirectState();
  }
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.customPOI {
  padding-bottom: $space-200;
  margin: $space-200 0 $space-200;
  border-bottom: 1px solid $color-grey-50;
}

.customPOIHeader {
  display: flex;
  align-items: flex-start;

  .customPOIHeaderRight {
    display: flex;
    gap: 1rem;
    margin-left: auto;

    svg {
      cursor: pointer;
    }

    &.loading {
      opacity: 0.5;

      svg {
        cursor: not-allowed;
      }
    }
  }
}

.customPOITitle {
  margin: 0;
}

.customPOIList {
  @include menu-ul-reset;

  display: grid;
  gap: $space-100;
  margin-top: $space-100;

  @media (min-width: $viewport-md-min) {
    grid-template-columns: repeat(2, 1fr);

    .customPOIListDescription {
      @include truncate-text;
    }
  }

  h4 {
    margin: $space-25 0;
  }
}

.customPOISingleItem {
  @include menu-ul-reset;

  margin-top: $space-100;

  @media (min-width: $viewport-md-min) {
    .customPOIListDescription {
      @include truncate-text;
    }
  }

  h4 {
    margin: $space-25 0;
  }
}

.customPOIListContent {
  display: flex;
  align-items: center;
  text-align: left;
}

.customPOIListTime {
  flex: 0 0 80px;
  margin-left: $space-25;
}

.customPOIListDescription {
  @media (min-width: $viewport-md-min) {
    @include truncate-text;
  }
}

.customPOITransparencyTitle {
  margin: 0;

  @include body-1-bold;
}

.customPOITransparencyBody {
  margin: $space-50 0 0 0;

  @include body-3;

  br {
    display: block;
    margin: $space-25 0 0 0;
    content: '';
  }
}
</style>
